import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { 
  ArrowRight,
  Star,
  Quote,
  Heart,
  Award,
  Phone
} from 'lucide-react';

const testimonials = [
  {
    id: 1,
    name: 'Margaret Johnson',
    relation: 'Patient, Boca Raton',
    rating: 5,
    text: 'The nurses from Senior Care Solutions have been absolutely wonderful. After my hip surgery, they helped me regain my independence faster than I ever thought possible. Their physical therapy team is exceptional!',
    service: 'Physical Therapy',
  },
  {
    id: 2,
    name: 'Robert Williams',
    relation: "Son of Patient, Delray Beach",
    rating: 5,
    text: "When my mother was diagnosed with CHF, we were overwhelmed. Senior Care Solutions stepped in and provided incredible cardiac care. The nurses were always available and helped us understand how to manage her condition. Mom is doing so much better now.",
    service: 'Cardiac Care',
  },
  {
    id: 3,
    name: 'Patricia Martinez',
    relation: 'Patient, Palm Beach Gardens',
    rating: 5,
    text: 'After my stroke, I struggled with speech and swallowing. The speech therapist from Senior Care Solutions was patient and encouraging. Now I can enjoy meals with my family again. Forever grateful!',
    service: 'Speech Therapy',
  },
  {
    id: 4,
    name: 'James Anderson',
    relation: 'Patient, Wellington',
    rating: 5,
    text: 'The wound care I received was exceptional. My surgical wound that had been slow to heal finally closed properly thanks to their skilled nursing team. They visited regularly and kept my doctor informed.',
    service: 'Wound Care',
  },
  {
    id: 5,
    name: 'Susan Thompson',
    relation: 'Daughter of Patient, Boynton Beach',
    rating: 5,
    text: "Dad has Parkinson's and the occupational therapy has made such a difference. The therapist taught him techniques to maintain his independence and even showed us how to make the home safer. Truly life-changing.",
    service: 'Occupational Therapy',
  },
  {
    id: 6,
    name: 'Dorothy Chen',
    relation: 'Patient, Jupiter',
    rating: 5,
    text: 'At 87, I was worried about being able to stay in my home. Senior Care Solutions gave me the support I needed to remain independent. The staff treats me like family, and I feel safe knowing help is always a phone call away.',
    service: 'Skilled Nursing',
  },
  {
    id: 7,
    name: 'William Garcia',
    relation: 'Husband of Patient, Coral Springs',
    rating: 5,
    text: 'My wife needed IV antibiotics at home after a serious infection. The infusion therapy nurses were so skilled and compassionate. They made a scary situation manageable and allowed my wife to recover in the comfort of our home.',
    service: 'Infusion Therapy',
  },
  {
    id: 8,
    name: 'Nancy Taylor',
    relation: 'Patient, Lake Worth',
    rating: 5,
    text: "The diabetes education I received has changed my life. The nurse took the time to explain everything clearly and helped me understand how to manage my blood sugar. I feel more in control of my health than ever before.",
    service: 'Diabetic Care',
  },
];

const stats = [
  { value: '98%', label: 'Patient Satisfaction' },
  { value: '5,000+', label: 'Families Served' },
  { value: '4.9/5', label: 'Average Rating' },
  { value: '15+', label: 'Years of Service' },
];

export const Testimonials = () => {
  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="relative py-20 bg-[#000080]" data-testid="testimonials-hero">
        <div className="absolute inset-0 bg-gradient-to-r from-[#000080] to-[#1E3A8A]"></div>
        <div className="container mx-auto px-4 md:px-6 lg:px-8 relative z-10">
          <div className="max-w-3xl">
            <span className="inline-block text-[#34D399] font-semibold mb-4">TESTIMONIALS</span>
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-6">
              What Our Patients Say
            </h1>
            <p className="text-xl text-white/80">
              Hear from the families we've had the privilege to serve. Their stories 
              inspire us to continue providing exceptional care every day.
            </p>
          </div>
        </div>
      </section>

      {/* Stats */}
      <section className="py-10 bg-[#ECFDF5]" data-testid="testimonials-stats">
        <div className="container mx-auto px-4 md:px-6 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {stats.map((stat, index) => (
              <div key={index} className="text-center">
                <p className="text-3xl md:text-4xl font-bold text-[#00A878] mb-1">{stat.value}</p>
                <p className="text-[#334155]">{stat.label}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Featured Testimonials */}
      <section className="py-16 bg-white" data-testid="featured-testimonials">
        <div className="container mx-auto px-4 md:px-6 lg:px-8">
          <div className="text-center max-w-3xl mx-auto mb-12">
            <span className="inline-block text-[#00A878] font-semibold mb-4">PATIENT STORIES</span>
            <h2 className="text-3xl md:text-4xl font-bold text-[#0F172A] mb-4">
              Stories That Inspire Us
            </h2>
            <p className="text-lg text-[#334155]">
              Every patient has a unique journey, and we're honored to be part of theirs.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-12">
            {testimonials.slice(0, 4).map((testimonial) => (
              <Card key={testimonial.id} className="bg-[#F8FAFC] border-l-4 border-[#00A878] h-full">
                <CardContent className="p-6">
                  <div className="flex items-center gap-1 mb-3">
                    {[...Array(testimonial.rating)].map((_, i) => (
                      <Star key={i} className="w-4 h-4 fill-[#00A878] text-[#00A878]" />
                    ))}
                  </div>
                  <Quote className="w-8 h-8 text-[#00A878] opacity-20 mb-3" />
                  <p className="text-[#334155] leading-relaxed mb-4">
                    "{testimonial.text}"
                  </p>
                  <div className="border-t border-slate-200 pt-3">
                    <p className="font-semibold text-[#0F172A]">{testimonial.name}</p>
                    <p className="text-sm text-[#334155]">{testimonial.relation}</p>
                    <span className="inline-block mt-2 text-xs bg-[#ECFDF5] text-[#065F46] px-3 py-1 rounded-full">
                      {testimonial.service}
                    </span>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* All Testimonials Grid */}
      <section className="py-16 bg-[#F8FAFC]" data-testid="all-testimonials">
        <div className="container mx-auto px-4 md:px-6 lg:px-8">
          <div className="text-center max-w-3xl mx-auto mb-12">
            <span className="inline-block text-[#00A878] font-semibold mb-4">MORE REVIEWS</span>
            <h2 className="text-3xl md:text-4xl font-bold text-[#0F172A] mb-4">
              More From Our Patients
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {testimonials.map((testimonial, index) => (
              <Card 
                key={testimonial.id}
                className="bg-white border border-slate-100 hover:shadow-lg transition-shadow"
                data-testid={`testimonial-card-${index}`}
              >
                <CardContent className="p-5">
                  <div className="flex items-center gap-1 mb-2">
                    {[...Array(testimonial.rating)].map((_, i) => (
                      <Star key={i} className="w-4 h-4 fill-[#00A878] text-[#00A878]" />
                    ))}
                  </div>
                  <p className="text-[#334155] text-sm leading-relaxed mb-3">
                    "{testimonial.text}"
                  </p>
                  <div className="border-t border-slate-100 pt-3">
                    <p className="font-semibold text-[#0F172A] text-sm">{testimonial.name}</p>
                    <p className="text-xs text-[#334155]">{testimonial.relation}</p>
                    <span className="inline-block mt-2 text-xs bg-[#ECFDF5] text-[#065F46] px-2 py-1 rounded-full">
                      {testimonial.service}
                    </span>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Trust Section */}
      <section className="py-16 bg-white" data-testid="trust-section">
        <div className="container mx-auto px-4 md:px-6 lg:px-8">
          <div className="bg-gradient-to-r from-[#000080] to-[#1E3A8A] rounded-2xl p-10 text-center">
            <div className="flex justify-center gap-4 mb-6">
              <div className="w-14 h-14 rounded-full bg-white/10 flex items-center justify-center">
                <Award className="w-7 h-7 text-[#34D399]" />
              </div>
              <div className="w-14 h-14 rounded-full bg-white/10 flex items-center justify-center">
                <Heart className="w-7 h-7 text-[#34D399]" />
              </div>
              <div className="w-14 h-14 rounded-full bg-white/10 flex items-center justify-center">
                <Star className="w-7 h-7 text-[#34D399]" />
              </div>
            </div>
            <h2 className="text-2xl md:text-3xl font-bold text-white mb-4">
              Trusted by Thousands of Florida Families
            </h2>
            <p className="text-lg text-white/80 mb-6 max-w-2xl mx-auto">
              Our commitment to excellence has earned us the trust of families throughout 
              South Florida.
            </p>
            <div className="flex flex-wrap justify-center gap-3">
              <div className="bg-white/10 rounded-full px-5 py-2">
                <span className="text-white font-medium text-sm">Medicare Certified</span>
              </div>
              <div className="bg-white/10 rounded-full px-5 py-2">
                <span className="text-white font-medium text-sm">CHAP Accredited</span>
              </div>
              <div className="bg-white/10 rounded-full px-5 py-2">
                <span className="text-white font-medium text-sm">ACHC Accredited</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-[#ECFDF5]" data-testid="testimonials-cta">
        <div className="container mx-auto px-4 md:px-6 lg:px-8 text-center">
          <h2 className="text-2xl md:text-3xl font-bold text-[#0F172A] mb-4">
            Ready to Experience Our Care?
          </h2>
          <p className="text-lg text-[#334155] mb-8 max-w-2xl mx-auto">
            Join the thousands of families who have trusted Senior Care Solutions with 
            their loved ones. Contact us today for a free consultation.
          </p>
          <div className="flex flex-col sm:flex-row justify-center gap-4">
            <Link to="/contact" data-testid="testimonials-cta-contact">
              <Button 
                size="lg" 
                className="bg-[#00A878] hover:bg-[#065F46] text-white rounded-full px-8 py-6 text-lg font-semibold"
              >
                Request a Consultation
                <ArrowRight className="w-5 h-5 ml-2" />
              </Button>
            </Link>
            <a href="tel:+18773106120" data-testid="testimonials-cta-call">
              <Button 
                size="lg" 
                variant="outline"
                className="border-2 border-[#000080] text-[#000080] hover:bg-[#000080] hover:text-white rounded-full px-8 py-6 text-lg font-semibold"
              >
                <Phone className="w-5 h-5 mr-2" />
                Call 877.310.6120
              </Button>
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Testimonials;
